import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveLine } from '@nivo/line';
import DateInput from '../shared/inputs/DateInput.js';
import DashAwareLines from '../charts/reproduction/DashAwareLines.js';
import '../charts/reproduction/NivoChartCard.css';
import './WeightLineChart.css';

const ACTUAL_COLOR = '#b8862f';
const PROJECTION_COLOR = '#c87800';

const formatLabel = (dateStr) => {
  const d = new Date(dateStr.length === 10 ? dateStr + 'T12:00:00' : dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
};

/**
 * WeightLineChart — line chart with optional projection inputs.
 *
 * Props:
 *   points  — [{ date: 'YYYY-MM-DD', weight: number }]
 *   label   — dataset label string
 */
const WeightLineChart = ({ points = [], label = '' }) => {
  const { t } = useTranslation();
  const [targetDate,   setTargetDate]   = useState('');
  const [targetWeight, setTargetWeight] = useState('');

  if (points.length === 0) return null;

  // Normalize to a plain YYYY-MM-DD once, in case a caller passes a full ISO
  // timestamp — every day-math calculation below assumes a bare date.
  const sorted = [...points]
    .filter(p => p.date)
    .map(p => ({ ...p, date: p.date.slice(0, 10) }))
    .sort((a, b) => a.date.localeCompare(b.date));
  const first  = sorted[0];
  const last   = sorted[sorted.length - 1];

  // ADG computed from first to last actual point
  const adg = (() => {
    if (sorted.length < 2 || first.date === last.date) return null;
    const days = (new Date(last.date + 'T12:00:00') - new Date(first.date + 'T12:00:00')) / 86400000;
    return days > 0 ? (last.weight - first.weight) / days : null;
  })();

  // Projection: extend to a chosen date
  const projByDate = (() => {
    if (!targetDate || !adg || targetDate <= last.date) return null;
    const days = (new Date(targetDate + 'T12:00:00') - new Date(last.date + 'T12:00:00')) / 86400000;
    return { date: targetDate, weight: parseFloat((last.weight + adg * days).toFixed(1)) };
  })();

  // Projection: find date for a chosen target weight
  const projByWeight = (() => {
    if (!targetWeight || !adg || adg === 0) return null;
    const tw = parseFloat(targetWeight);
    if (!tw) return null;
    // Works for both positive ADG (gaining) and negative (losing)
    const days = Math.round((tw - last.weight) / adg);
    if (days <= 0) return null;
    const d = new Date(last.date + 'T12:00:00');
    d.setDate(d.getDate() + days);
    const projDate = d.toISOString().split('T')[0];
    return { date: projDate, weight: tw };
  })();

  // Average spacing between actual data points (in days), used to interpolate projection
  const avgSpacingDays = (() => {
    if (sorted.length < 2) return 7;
    const totalDays = (new Date(last.date + 'T12:00:00') - new Date(first.date + 'T12:00:00')) / 86400000;
    return Math.max(1, Math.round(totalDays / (sorted.length - 1)));
  })();

  // Generate interpolated points from last actual date to projPoint at avgSpacingDays intervals
  const interpolateProjection = (projPoint) => {
    if (!projPoint || !adg) return [];
    const startMs  = new Date(last.date + 'T12:00:00').getTime();
    const endMs    = new Date(projPoint.date + 'T12:00:00').getTime();
    const totalDays = (endMs - startMs) / 86400000;
    const pts = [];
    for (let d = avgSpacingDays; d < totalDays; d += avgSpacingDays) {
      const dt = new Date(startMs + d * 86400000);
      pts.push({
        date:   dt.toISOString().split('T')[0],
        weight: parseFloat((last.weight + adg * d).toFixed(1)),
      });
    }
    pts.push(projPoint);
    return pts;
  };

  const projByDatePoints   = interpolateProjection(projByDate);
  const projByWeightPoints = interpolateProjection(projByWeight);

  // Merge all dates into one sorted shared x-domain (point scale)
  const allDates = [...new Set([
    ...sorted.map(p => p.date),
    ...projByDatePoints.map(p => p.date),
    ...projByWeightPoints.map(p => p.date),
  ])].sort();

  const actualSeriesData = sorted.map(p => ({ x: p.date, y: p.weight }));

  // Projection series — connect from the last actual point through the
  // interpolated points, null everywhere else on the shared domain so the
  // dashed run starts exactly at the last real point.
  const makeProjectionSeries = (projPoints) => {
    if (!projPoints.length) return null;
    const projMap = Object.fromEntries(projPoints.map(p => [p.date, p.weight]));
    return allDates.map(date => {
      if (date === last.date) return { x: date, y: last.weight };
      if (date in projMap)    return { x: date, y: projMap[date] };
      return { x: date, y: null };
    });
  };

  const series = [
    { id: label, color: ACTUAL_COLOR, data: actualSeriesData },
  ];

  const projDateData   = makeProjectionSeries(projByDatePoints);
  const projWeightData = makeProjectionSeries(projByWeightPoints);

  if (projDateData) {
    series.push({ id: t('proj_by_date_label'), color: PROJECTION_COLOR, dashed: true, data: projDateData });
  }
  if (projWeightData) {
    series.push({ id: t('proj_by_weight_label'), color: PROJECTION_COLOR, dashed: true, data: projWeightData });
  }

  // Thin x-axis ticks to at most ~7, evenly spaced by INDEX POSITION (not a
  // fixed stride) — interpolated projection points cluster far more densely
  // than the real weigh-in dates, so a modulo stride leaves visually uneven
  // gaps; picking evenly-spaced positions across the full array avoids that.
  const maxTicks = Math.min(5, allDates.length);
  const tickValues = Array.from({ length: maxTicks }, (_, i) =>
    allDates[Math.round((i * (allDates.length - 1)) / Math.max(1, maxTicks - 1))]
  ).filter((v, i, arr) => arr.indexOf(v) === i);

  return (
    <div className="wlc-wrap">
      <div className="wlc-chart-wrap" style={{ height: 300 }}>
        <ResponsiveLine
          data={series}
          margin={{ top: 12, right: 16, bottom: series.length > 1 ? 86 : 56, left: 52 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', nice: true }}
          axisBottom={{ tickValues, format: formatLabel, tickRotation: tickValues.length > 4 ? -30 : 0 }}
          axisLeft={{ format: (v) => `${v}kg` }}
          colors={{ datum: 'color' }}
          lineWidth={2}
          pointSize={sorted.length > 20 ? 3 : 5}
          useMesh={true}
          enableGridX={false}
          motionConfig="gentle"
          layers={['grid', 'axes', 'crosshair', DashAwareLines, 'points', 'slices', 'mesh', 'legends']}
          legends={series.length > 1 ? [{
            anchor: 'bottom', direction: 'row', translateY: 76, itemWidth: 120, itemHeight: 14, itemsSpacing: 8, symbolSize: 10,
          }] : []}
          theme={{
            axis: { ticks: { text: { fill: '#898781', fontSize: 11.5 } } },
            grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
            legends: { text: { fill: '#52514e', fontSize: 12 } },
          }}
          tooltip={({ point }) => (
            <div className="nivo-tooltip">
              <span className="nivo-tooltip-swatch" style={{ background: point.seriesColor }} />
              <strong>{point.seriesId}</strong>: <span className="nivo-tooltip-value">{Number(point.data.y).toFixed(1)} kg</span>
            </div>
          )}
        />
      </div>

      {adg && (
        <div className="wlc-projection-row">
          <div className="wlc-proj-field">
            <label className="wlc-proj-label">{t('proj_target_date')}</label>
            <DateInput
              className="wlc-proj-input"
              value={targetDate}
              min={last.date}
              required={false}
              defaultToToday={false}
              onChange={(v) => { setTargetDate(v); setTargetWeight(''); }}
            />
            {projByDate && (
              <span className="wlc-proj-result">≈ {projByDate.weight} kg</span>
            )}
          </div>
          <div className="wlc-proj-field">
            <label className="wlc-proj-label">{t('proj_target_weight')}</label>
            <input
              type="number"
              className="wlc-proj-input"
              value={targetWeight}
              min="1"
              max="9999"
              step="0.1"
              placeholder={t('unit_kg')}
              onChange={e => { setTargetWeight(e.target.value); setTargetDate(''); }}
            />
            {projByWeight && (
              <span className="wlc-proj-result">≈ {formatLabel(projByWeight.date)}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeightLineChart;
